import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PlanGate from '../components/PlanGate';
import BrunoMessage from '../components/BrunoMessage';
import CurrencyDisplay from '../components/CurrencyDisplay';
import api from '../lib/axios';

const TIPO_LABELS = {
  casa_terrea: 'Casa Térrea',
  sobrado: 'Sobrado',
  apartamento: 'Apartamento',
  comercial: 'Comercial',
  galpao: 'Galpão Industrial',
};

const formatCurrency = (value) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value || 0);

const OrcamentoResultadoNew = () => {
  const { id } = useParams();
  const [resultado, setResultado] = useState(null);
  const [orcamento, setOrcamento] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Check localStorage first
        const cachedResult = localStorage.getItem(`orcamento_resultado_${id}`);
        if (cachedResult) {
          setResultado(JSON.parse(cachedResult));
        }

        // Fetch full data from API
        const response = await api.get(`/api/orcamento/${id}`);
        setOrcamento(response.data);

        if (response.data.resultado) {
          setResultado(response.data.resultado);
        }

        setLoading(false);
      } catch (err) {
        console.error('Erro ao carregar orçamento:', err);
        setError('Erro ao carregar orçamento. Tente novamente.');
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-muted/30">
        <Header />
        <div className="flex items-center justify-center py-20">
          <p className="text-muted-foreground">Carregando resultado...</p>
        </div>
      </div>
    );
  }

  if (error || !resultado || !orcamento) {
    return (
      <div className="min-h-screen bg-muted/30">
        <Header />
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <p className="text-red-600 mb-4">{error || 'Resultado não encontrado'}</p>
            <Link
              to="/orcamento/novo"
              className="inline-block px-6 py-2 bg-primary text-white rounded-lg hover:opacity-90"
            >
              Fazer Nova Análise
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const {
    tipo_construcao,
    area_total,
    estado,
    cub_codigo,
    cub_valor,
    valor_total,
    valor_construcao,
    etapas = [],
    itens_especiais = [],
  } = resultado;

  const tipoLabel = TIPO_LABELS[tipo_construcao] || tipo_construcao;
  const valorPorM2 = area_total > 0 ? valor_total / area_total : 0;
  const totalItensEspeciais = itens_especiais.reduce(
    (acc, item) => acc + (item.valor || 0),
    0
  );

  const maxPercentual = Math.max(...etapas.map(e => e.percentual || 0), 1);

  const handleExportPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    let y = 15;

    // Header bar
    doc.setFillColor(15, 23, 42);
    doc.rect(0, 0, pageWidth, 35, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('QuantoCustaObra', margin, 16);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text('quantocustaobra.com.br', margin, 23);
    doc.text(new Date().toLocaleDateString('pt-BR'), pageWidth - margin, 16, { align: 'right' });
    doc.text('Orçamento por Planta', pageWidth - margin, 23, { align: 'right' });
    y = 45;

    // Title
    doc.setTextColor(15, 23, 42);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Orçamento de Obra — Estimativa por Planta', margin, y);
    y += 12;

    // Project info box
    doc.setFillColor(248, 250, 252);
    doc.setDrawColor(226, 232, 240);
    doc.roundedRect(margin, y, pageWidth - margin * 2, 24, 3, 3, 'FD');
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(71, 85, 105);
    const col2x = pageWidth / 2 + 5;
    doc.text(`Tipo: ${tipoLabel}`, margin + 5, y + 8);
    doc.text(`Estado: ${estado}`, margin + 5, y + 16);
    doc.text(`Area Total: ${area_total} m2`, col2x, y + 8);
    doc.text(`CUB Base: ${formatCurrency(cub_valor)}/m2`, col2x, y + 16);
    y += 32;

    // Etapas table
    if (etapas.length > 0) {
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(15, 23, 42);
      doc.text('Etapas da Obra', margin, y);
      y += 4;

      const etapasRows = etapas.map((e) => {
        const etapaValor = (valor_construcao * (e.percentual || 0)) / 100;
        return [e.nome, `${e.percentual}%`, formatCurrency(etapaValor)];
      });
      etapasRows.push([
        { content: 'Total Construção', styles: { fontStyle: 'bold' } },
        { content: '100%', styles: { fontStyle: 'bold', halign: 'center' } },
        { content: formatCurrency(valor_construcao), styles: { fontStyle: 'bold', halign: 'right' } },
      ]);

      doc.autoTable({
        startY: y,
        margin: { left: margin, right: margin },
        head: [['Etapa', '%', 'Valor (R$)']],
        body: etapasRows,
        theme: 'striped',
        headStyles: { fillColor: [15, 23, 42], fontSize: 8, fontStyle: 'bold' },
        bodyStyles: { fontSize: 8 },
        columnStyles: { 1: { halign: 'center' }, 2: { halign: 'right' } },
      });
      y = doc.lastAutoTable.finalY + 8;
    }

    // Itens Especiais
    if (itens_especiais.length > 0) {
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(15, 23, 42);
      doc.text('Itens Especiais', margin, y);
      y += 4;

      const itensRows = itens_especiais.map((i) => [i.nome, formatCurrency(i.valor || 0)]);
      itensRows.push([
        { content: 'Subtotal Itens Especiais', styles: { fontStyle: 'bold' } },
        { content: formatCurrency(totalItensEspeciais), styles: { fontStyle: 'bold', halign: 'right' } },
      ]);

      doc.autoTable({
        startY: y,
        margin: { left: margin, right: margin },
        head: [['Item', 'Valor (R$)']],
        body: itensRows,
        theme: 'striped',
        headStyles: { fillColor: [71, 85, 105], fontSize: 8, fontStyle: 'bold' },
        bodyStyles: { fontSize: 8 },
        columnStyles: { 1: { halign: 'right' } },
      });
      y = doc.lastAutoTable.finalY + 8;
    }

    // Total highlight box
    doc.setFillColor(249, 115, 22);
    doc.roundedRect(margin, y, pageWidth - margin * 2, 20, 3, 3, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('CUSTO TOTAL ESTIMADO', margin + 8, y + 9);
    doc.setFontSize(14);
    doc.text(formatCurrency(valor_total), pageWidth - margin - 8, y + 9, { align: 'right' });
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(`${formatCurrency(valorPorM2)}/m2`, pageWidth - margin - 8, y + 16, { align: 'right' });
    y += 28;

    // Disclaimer
    doc.setFillColor(255, 251, 235);
    doc.setDrawColor(253, 230, 138);
    doc.roundedRect(margin, y, pageWidth - margin * 2, 14, 2, 2, 'FD');
    doc.setFontSize(7);
    doc.setTextColor(146, 64, 14);
    doc.setFont('helvetica', 'italic');
    doc.text('Este orçamento é uma estimativa baseada no CUB. Consulte um profissional para orçamento detalhado.', margin + 5, y + 9);

    // Footer
    const footerY = doc.internal.pageSize.getHeight() - 15;
    doc.setDrawColor(226, 232, 240);
    doc.line(margin, footerY - 5, pageWidth - margin, footerY - 5);
    doc.setFontSize(7);
    doc.setTextColor(148, 163, 184);
    doc.setFont('helvetica', 'normal');
    doc.text('Gerado por QuantoCustaObra.com.br — Estimativa baseada no CUB', margin, footerY);
    doc.text(`CUB ${cub_codigo} - ${estado} | SINDUSCON`, pageWidth - margin, footerY, { align: 'right' });

    doc.save(`QuantoCustaObra_Orcamento_${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  const handleExportExcel = () => {
    const rows = [
      ['Orçamento por Planta - Quanto Custa Obra'],
      [],
      ['Tipo', tipoLabel],
      ['Estado', estado],
      ['Area Total', `${area_total} m2`],
      ['CUB Base', `${formatCurrency(cub_valor)}/m2`],
      [],
      ['ETAPAS DA OBRA', '', ''],
      ['Etapa', '%', 'Valor (R$)'],
      ...etapas.map((e) => {
        const etapaValor = (valor_construcao * (e.percentual || 0)) / 100;
        return [e.nome, `${e.percentual}%`, etapaValor.toFixed(2)];
      }),
      ['Subtotal Construção', '100%', (valor_construcao || 0).toFixed(2)],
      [],
    ];

    if (itens_especiais.length > 0) {
      rows.push(['ITENS ESPECIAIS', '', '']);
      rows.push(['Item', '', 'Valor (R$)']);
      itens_especiais.forEach((i) => rows.push([i.nome, '', (i.valor || 0).toFixed(2)]));
      rows.push(['Subtotal Itens Especiais', '', totalItensEspeciais.toFixed(2)]);
      rows.push([]);
    }

    rows.push(['TOTAL ESTIMADO', '', (valor_total || 0).toFixed(2)]);
    rows.push(['Custo por m2', '', valorPorM2.toFixed(2)]);

    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws['!cols'] = [{ wch: 35 }, { wch: 15 }, { wch: 20 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Orçamento');
    const buffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `QuantoCustaObra_Orcamento_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  return (
    <div className="min-h-screen bg-muted/30">
      <Header />

      <div className="container mx-auto px-4 py-8 pt-32 max-w-6xl">
        {/* MAIN RESULT CARD */}
        <div className="bg-card rounded-2xl overflow-hidden shadow-card mb-8 border-t-4 border-primary">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 p-8">
            {/* Left: Main Info */}
            <div>
              {/* Header with type, area, state */}
              <div className="mb-6">
                <p className="text-muted-foreground text-sm font-medium mb-1">
                  {tipoLabel} • {area_total}m² • {estado}
                </p>
              </div>

              {/* Big value */}
              <div className="mb-4">
                <CurrencyDisplay value={valor_total} size="lg" />
              </div>

              {/* Value per m2 */}
              <div className="mb-6">
                <p className="text-muted-foreground text-sm">
                  {formatCurrency(valorPorM2)}/m²
                </p>
              </div>

              {/* Base info */}
              <div className="mb-8">
                <p className="text-muted-foreground text-xs">
                  Base: CUB {estado} {cub_codigo} • {formatCurrency(cub_valor)}/m²
                </p>
              </div>
            </div>

            {/* Right: Bruno image */}
            <div className="hidden md:flex items-center justify-center">
              <BrunoMessage pose="calculadora" message="Aqui esta o resultado do seu orçamento!" />
            </div>
          </div>
        </div>

        {/* DETALHAMENTO POR ETAPA */}
        {etapas.length > 0 && (
          <div className="bg-card rounded-2xl shadow-card p-8 mb-8">
            <h2 className="text-xl font-bold text-secondary mb-6">
              Detalhamento por Etapa
            </h2>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4 font-semibold text-foreground">
                      Etapa
                    </th>
                    <th className="text-left py-3 px-4 font-semibold text-foreground w-40">
                      Percentual
                    </th>
                    <th className="text-right py-3 px-4 font-semibold text-foreground">
                      Valor
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {etapas.map((etapa, index) => {
                    const etapaValor = (valor_construcao * (etapa.percentual || 0)) / 100;
                    const bgClass = index % 2 === 0 ? 'bg-white' : 'bg-muted';

                    return (
                      <tr key={index} className={`border-b border-border/50 ${bgClass}`}>
                        <td className="py-4 px-4 text-foreground font-medium">
                          {etapa.nome}
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-3">
                            <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                              <div
                                className="h-full bg-primary transition-all"
                                style={{
                                  width: `${(etapa.percentual / maxPercentual) * 100}%`,
                                }}
                              />
                            </div>
                            <span className="text-foreground font-medium w-12 text-right">
                              {etapa.percentual}%
                            </span>
                          </div>
                        </td>
                        <td className="py-4 px-4 text-right text-foreground font-medium">
                          {formatCurrency(etapaValor)}
                        </td>
                      </tr>
                    );
                  })}

                  {/* Total row */}
                  <tr className="bg-muted font-bold">
                    <td className="py-4 px-4 text-foreground">Total</td>
                    <td className="py-4 px-4 text-foreground">
                      <div className="flex items-center gap-3">
                        <div className="flex-1 h-2 bg-muted rounded-full" />
                        <span className="w-12 text-right">100%</span>
                      </div>
                    </td>
                    <td className="py-4 px-4 text-right text-foreground">
                      {formatCurrency(valor_construcao)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ITENS ESPECIAIS */}
        {itens_especiais.length > 0 && (
          <div className="bg-card rounded-2xl shadow-card p-8 mb-8">
            <h2 className="text-xl font-bold text-secondary mb-6">Itens Especiais</h2>

            <div className="space-y-3 mb-6">
              {itens_especiais.map((item, index) => (
                <div
                  key={index}
                  className={`flex justify-between items-center py-3 px-4 ${
                    index % 2 === 0 ? 'bg-white' : 'bg-muted'
                  } rounded-lg`}
                >
                  <span className="text-foreground">{item.nome}</span>
                  <span className="font-medium text-foreground">
                    {formatCurrency(item.valor || 0)}
                  </span>
                </div>
              ))}
            </div>

            {/* Subtotal */}
            <div className="pt-4 border-t border-border">
              <div className="flex justify-between items-center">
                <span className="font-semibold text-foreground">Subtotal Itens</span>
                <span className="font-bold text-foreground">
                  {formatCurrency(totalItensEspeciais)}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* RESUMO CARD */}
        <div className="bg-card rounded-2xl shadow-card p-8 mb-8">
          <h2 className="text-xl font-bold text-secondary mb-6">Resumo</h2>

          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-foreground">Valor Base (Construção)</span>
              <span className="text-foreground font-medium">
                {formatCurrency(valor_construcao)}
              </span>
            </div>

            {totalItensEspeciais > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-foreground">Itens Especiais</span>
                <span className="text-foreground font-medium">
                  {formatCurrency(totalItensEspeciais)}
                </span>
              </div>
            )}

            <div className="border-t-2 border-border pt-4 mt-4">
              <div className="flex justify-between items-center">
                <span className="text-lg font-bold text-secondary">TOTAL</span>
                <span className="text-3xl font-bold text-primary">
                  {formatCurrency(valor_total)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* ACTION BUTTONS */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <PlanGate minPlan="basico" inline>
            <button
              onClick={handleExportPDF}
              className="flex-1 px-6 py-3 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-all flex items-center justify-center gap-2 cursor-pointer"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              Baixar PDF
            </button>
          </PlanGate>
          <PlanGate minPlan="basico" inline>
            <button
              onClick={handleExportExcel}
              className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-all flex items-center justify-center gap-2 cursor-pointer"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Baixar Excel
            </button>
          </PlanGate>
          <Link
            to="/orcamento/novo"
            className="flex-1 px-6 py-3 border-2 border-border text-foreground rounded-lg font-semibold hover:bg-muted transition-all text-center"
          >
            Nova Análise
          </Link>
        </div>

        {/* UPSELL CARD */}
        <div className="bg-gradient-hero rounded-2xl shadow-card p-8 text-white overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            {/* Left: Image */}
            <div className="hidden md:flex items-center justify-center">
              <BrunoMessage pose="handshake" message="Quer um orçamento ainda mais detalhado com SINAPI?" />
            </div>

            {/* Right: Content */}
            <div>
              <h3 className="text-2xl font-bold mb-3">
                Quer orçamento DETALHADO com SINAPI?
              </h3>
              <p className="text-white/70 mb-6">
                Obtenha um orçamento completo com desdobramento de serviços e custos
                unitários baseados na tabela SINAPI. Ideal para projetos maiores e
                tomadas de decisão mais precisas.
              </p>
              <Link
                to="/orcamento-sinapi"
                className="inline-block px-6 py-3 bg-primary text-white rounded-lg font-semibold hover:opacity-90 transition-all"
              >
                Falar com Profissional
              </Link>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default OrcamentoResultadoNew;
